// Profil-Nachbau F-11 und F-09 (Bennys Freigaben 14.10.2026, BAU-FIRMA.md 1.6, PF-704): Wörter, Typen und reine Regeln der
// Seiten unter „Vorgaben für alle“ in den Einstellungen der Firma, ohne React, damit frontend/tests sie ohne Browser prüfen. Nicht
// zu verwechseln mit administration/vorgaben/vorgabenTexte.ts (Vorgaben des Betreibers für alle Firmen, PF-412). Der Produktname
// kommt vom Aufrufer (brand.displayName, nach MK-1 immer CLAiRIS), der Firmenname aus dem Profil. Was gilt, entscheidet der
// Server (organization/rechercheVorgaben.ts); die Seite zeigt nur seinen Stand.
// PF-705 (F-10, BAU-FIRMA.md 1.7): dazu die Seite „Benachrichtigungen“ (Firmenstandard je wählbarem Ereignis). Titel, Auslöser,
// Empfängerkreis und Gruppentitel kommen vom Server (notifications/katalog.ts); Typen und Aufrufe aus
// pages/einstellungen/benachrichtigungen.ts (PF-606).
// F-24 (PF-TX-firma-3, SPEC-firma-b.md 8 und 9, GRUNDREGELN 2): Unterzeilen höchstens 12 Wörter, der Rest im Tooltip bzw. im Fenster
// „Wie funktionieren die Vorgaben?“; je Kontingentregel fallen in „Kontingent und Guthaben“ die nicht auslösbaren Schalter weg.
import type { ErklaerInhalt } from '../../components/Erklaerung';
import type { KontingentOrganisation } from '../kontingent';
import type { FirmenstandardZeile, Gruppe } from '../../pages/einstellungen/benachrichtigungen';

/** Laden, Fehler und Speichern aller Seiten unter „Vorgaben für alle“ (Wortlaut wie F-09). */
export const FIRMEN_VORGABEN_TEXTE = {
  laden: 'Die Vorgaben werden geladen …',
  ladeFehler: 'Die Vorgaben konnten nicht geladen werden.',
  erneut: 'Erneut versuchen',
  speicherFehler: 'Die Einstellung konnte nicht gespeichert werden.',
} as const;

// Ohne Firmennamen (Profil noch nicht geladen) heißt es „die Verwaltung Ihrer Firma“.
const verwaltungVon = (firma: string) => (firma.trim() ? `die Verwaltung von ${firma.trim()}` : 'die Verwaltung Ihrer Firma');

/** Seite „Recherche“ (BAU-FIRMA.md 1.6 wörtlich). */
export const RECHERCHE_VORGABEN_TEXTE = {
  titel: 'Recherche',
  beschreibung: 'Was bei Recherchen für alle in Ihrer Firma gilt.',
  websuche: 'Websuche',
  schalter: 'Websuche bei Recherchen erlauben',
  // F-24: der Tooltip am Schalter; sichtbar bleibt zusatz (Pflichthinweis Websuche bei Google).
  erklaerung: (produkt: string) => 'Wenn an, entscheidet jede Person in ihren Präferenzen selbst über die Websuche. '
    + `Wenn aus, sucht ${produkt} für niemanden im Web.`,
  zusatz: 'Bei der Websuche gehen aus der Frage gebildete Suchbegriffe an die Google-Suche.',
  gespeichert: 'Gespeichert. Gilt ab der nächsten Recherche.',
  // Muster SICHERHEIT_TEXTE.ohneRecht (sicherheitTexte.ts).
  ohneRecht: (firma: string) => `Ob die Websuche bei Recherchen erlaubt ist, legt ${verwaltungVon(firma)} fest.`,
  nichtVerfuegbar: 'In dieser Recherche-Art nicht verfügbar.',
} as const;

/** Seite „Benachrichtigungen“ (BAU-FIRMA.md 1.7, AUFTRAG-PF-705 Abschnitt 4 wörtlich). */
export const BENACHRICHTIGUNGS_VORGABEN_TEXTE = {
  titel: 'Benachrichtigungen',
  // F-24: der zweite Satz und der frühere Kasten zur Sicherheit stehen im Fenster (benachrichtigungsErklaerung).
  beschreibung: (_produkt: string) => 'Welche E-Mails Personen bekommen, solange sie nichts anderes wählen.',
  vorgabe: (produkt: string, an: boolean) => `Vorgabe von ${produkt}: ${an ? 'an' : 'aus'}`,
  uebernehmen: 'Vorgabe übernehmen',
  gespeichert: 'Gespeichert.',
  ohneRecht: (firma: string) => `Welche E-Mails die Personen bekommen, legt ${verwaltungVon(firma)} fest.`,
  selbstGewaehlt: (n: number) => (n === 1 ? '1 Person hat selbst gewählt.' : `${n} Personen haben selbst gewählt.`),
  // F-23 (PF-NB-firma-1, SPEC-firma-b Befunde 1 und 5): der Weg zu den eigenen Mails, die Beschriftung des Empfängerkreises und der
  // Satz der internen Organisation (keine Firmenebene der Mails, notifications/kontoart.ts hatFirmenebene).
  eigeneMails: { vor: 'Ihre eigenen E-Mails stellen Sie unter „', link: 'Meine Einstellungen › Benachrichtigungen', nach: '“ ein.' },
  gehtAn: 'Geht an: ',
  intern: (produkt: string) => `Für die interne Organisation gibt es keinen Firmenstandard für E-Mails. Es gilt die Vorgabe von ${produkt}; jede Person stellt ihre E-Mails unter „Meine Einstellungen › Benachrichtigungen“ ein.`,
  zuEigenen: 'Zu „Benachrichtigungen“ in „Meine Einstellungen“',
  // F-24 (E-5, Frage F-24-firma-2): die Zeile der Gruppe „Kontingent und Guthaben“ einer Firma ohne Kontingent; „Weitere …“, wenn
  // Schalter bleiben.
  ohneKontingent: 'Diese E-Mails gibt es erst, wenn Ihre Firma ein Kontingent hat.',
  ohneKontingentWeitere: 'Weitere E-Mails zu Kontingent und Guthaben gibt es erst, wenn Ihre Firma ein Kontingent hat.',
  ohneFirmenkontingent: 'Die E-Mail zum Kontingent der Firma gibt es erst, wenn Ihre Firma ein Kontingent hat.',
  ohneGuthabenSperre: 'E-Mails zum Guthaben gibt es nur, wenn bei erreichtem Kontingent neue Recherchen gesperrt werden.',
} as const;

/** F-24: das Fenster „Wie funktionieren die Vorgaben?“ im Kopf der Seite „Benachrichtigungen“ der Firma (SPEC-firma-b.md 9 wörtlich). */
export const benachrichtigungsErklaerung = (produkt: string): ErklaerInhalt => ({
  frage: 'Wie funktionieren die Vorgaben?',
  teile: [
    { titel: '1. Vorgabe der Firma', text: 'Sie legen fest, welche E-Mails die Personen in Ihrer Firma bekommen, solange sie selbst nichts anderes wählen.' },
    { titel: '2. Eigene Wahl', text: `Jede Person kann unter „Meine Einstellungen › Benachrichtigungen“ selbst wählen. In ${produkt} selbst erscheint jedes Ereignis immer.` },
    { titel: '3. Immer an', text: 'Mails zur Sicherheit eines Kontos gehen immer an die betroffene Person; sie lassen sich nicht abschalten.' },
  ],
});

/** Schalter der Gruppe „Kontingent und Guthaben“ mit eigener Kontingentregel; gespeicherte Firmenwerte bleiben beim Ausblenden.
 *  Die übrigen vier lösen auch ohne Kontingent aus (Bericht PF-TX-firma-3). */
export const NUR_MIT_KONTINGENT: readonly string[] = [
  // F-24 (PF-TX-firma-3): geprüft an backend/src/administration/notifications/schwellen.ts:112: mitKontingent (billing/kontingent.ts:419).
  'kontingent.firma',
  // F-24 (PF-TX-firma-3): geprüft an backend/src/administration/billing/guthaben.ts:299: hatKontingent (:171-173) für guthabenLage.
  'kontingent.guthaben_niedrig',
  // F-24 (PF-TX-firma-3): geprüft an backend/src/administration/billing/service.ts:753 → guthaben.ts:376: aufladenMoeglich (:183) mit hatKontingent (:171-173); Meldung :358.
  'kontingent.guthaben_aufgeladen',
];

/** Nicht auslösbare E-Mails je Schlüssel; ohne geladenen Stand nichts ausblenden, Guthaben folgt dem Serverflag. */
export function verborgeneKontingentSchluessel(org: Pick<KontingentOrganisation, 'kosten' | 'mitKontingent' | 'hatKontingent'> | null | undefined): readonly string[] {
  if (!org) return [];
  // Warnschwellen benötigen einen Kostenbalken mit positivem Nenner.
  const mitKontingent = !!org.kosten && org.mitKontingent !== false;
  // Die Guthabenregel kommt vom Server, unabhängig von positivem Budget und sichtbarem Guthaben.
  return NUR_MIT_KONTINGENT.filter(schluessel => schluessel === 'kontingent.firma' ? !mitKontingent : !org.hatKontingent);
}

/** Abschnitt „Glossar für die Suche“ auf der Seite „Recherche“ (AUFTRAG-PF-733 Abschnitt 4 wörtlich, F-20 Ergänzung 4). */
export const GLOSSAR_TEXTE = {
  titel: 'Glossar für die Suche',
  // F-24: der zweite Satz im Tooltip am Abschnittstitel.
  beschreibung: (_produkt: string) => 'Abkürzung und Langform gelten als gleich, etwa „KV“ und „Kollektivvertrag“.',
  tipp: (produkt: string) => `${produkt} bringt österreichische Begriffe mit; hier ergänzen Sie die Begriffe Ihrer Firma.`,
  hinzufuegen: 'Begriff hinzufügen …',
  grenze: (hoechstens: number) => `Ihre Firma hat schon ${hoechstens} Begriffe.`,
  tabelle: 'Begriffe Ihrer Firma',
  kurz: 'Abkürzung',
  lang: 'Langform',
  aktion: 'Aktion',
  weitereAktionen: (kurz: string) => `Weitere Aktionen für ${kurz}`,
  bearbeiten: 'Bearbeiten …',
  entfernen: 'Entfernen …',
  leer: (produkt: string) => `Noch keine eigenen Begriffe. Die Begriffe von ${produkt} gelten trotzdem.`,
  vorgaben: (produkt: string, anzahl: number) => `Begriffe von ${produkt} (${anzahl})`,
  dialogNeu: 'Begriff hinzufügen',
  dialogBearbeiten: 'Begriff bearbeiten',
  kurzHinweis: 'Bis 30 Zeichen, etwa „BV“.',
  langHinweis: 'Bis 150 Zeichen, etwa „Betriebsvereinbarung“.',
  abbrechen: 'Abbrechen',
  speichern: 'Begriff speichern',
  gespeichert: 'Der Begriff ist gespeichert. Er gilt ab der nächsten Recherche.',
  entfernenFrage: (kurz: string) => `„${kurz}“ entfernen?`,
  entfernenSatz: 'Die Suche behandelt die beiden Begriffe danach nicht mehr als gleichwertig.',
  entfernenKnopf: 'Entfernen',
  entfernt: 'Der Begriff ist entfernt.',
  ohneRecht: (firma: string) => `Das Glossar Ihrer Firma pflegt ${verwaltungVon(firma)}.`,
} as const;

/** Die Sätze des Servers (backend/src/administration/glossar/glossar.ts GLOSSAR_SAETZE, PF-731) für die Prüfung vor dem Senden. */
export const GLOSSAR_SAETZE = {
  kurz: 'Bitte eine Abkürzung mit 1 bis 30 Zeichen eingeben.',
  lang: 'Bitte die Langform mit 2 bis 150 Zeichen eingeben.',
  doppelt: 'Diesen Begriff gibt es schon.',
  grenze: 'Ihre Firma hat schon 200 Begriffe. Bitte zuerst einen entfernen.',
  veraltet: 'Der Begriff wurde inzwischen geändert. Bitte neu laden.',
  fehlt: 'Diesen Begriff gibt es nicht mehr.',
} as const;
export const GLOSSAR_LAENGE = { kurz: 30, lang: 150 } as const;

/** Ein eigener Begriff der Firma und die Antwort von GET /organizations/:id/glossar (PF-731 4.2). */
export interface GlossarBegriff { id: string; kurz: string; lang: string; revision: number }
export interface GlossarAntwort { eigene: GlossarBegriff[]; vorgaben: { kurz: string; lang: string }[]; kannAendern: boolean; hoechstens: number }

// Länge in Zeichen wie char_length am Server (ein Zeichen außerhalb der Grundebene zählt einmal).
const zeichen = (wert: string) => [...wert.trim()].length;
/** Prüfung vor dem Senden mit den Sätzen des Servers; Zeilenumbrüche und Steuerzeichen prüft der Server mit denselben Sätzen. */
export function begriffFehler(kurz: string, lang: string): { kurz?: string; lang?: string } {
  return {
    ...(zeichen(kurz) < 1 || zeichen(kurz) > GLOSSAR_LAENGE.kurz ? { kurz: GLOSSAR_SAETZE.kurz } : {}),
    ...(zeichen(lang) < 2 || zeichen(lang) > GLOSSAR_LAENGE.lang ? { lang: GLOSSAR_SAETZE.lang } : {}),
  };
}
/** Das Feld zu einem Code des Servers: GLOSSAR_KURZ und GLOSSAR_DOPPELT an „Abkürzung“, GLOSSAR_LANG an „Langform“; sonst null
 *  (der Satz steht am Formular). */
export function glossarFeld(code: string | null): 'kurz' | 'lang' | null {
  if (code === 'GLOSSAR_KURZ' || code === 'GLOSSAR_DOPPELT') return 'kurz';
  return code === 'GLOSSAR_LANG' ? 'lang' : null;
}
/** Alphabetisch nach der Abkürzung (de), bei gleicher Abkürzung nach der Langform – wie der Server (nachKurz). */
export const glossarSortiert = <B extends { kurz: string; lang: string }>(liste: readonly B[]) =>
  [...liste].sort((a, b) => a.kurz.localeCompare(b.kurz, 'de') || a.lang.localeCompare(b.lang, 'de'));
/** Grenze der Firma erreicht: „Begriff hinzufügen …“ gesperrt mit Satz. */
export const glossarVoll = (antwort: Pick<GlossarAntwort, 'eigene' | 'hoechstens'>) => antwort.eigene.length >= antwort.hoechstens;

/** Was der Abschnitt zeigt: laedt, fehler, nichtLesbar (Sicht; der Server antwortet 403 OPERATOR_VIEW_SCOPE), ohneRecht (nur
 *  Werte und der Satz zur Verwaltung) oder erlaubt (Hinzufügen, Bearbeiten, Entfernen). Schreiben nur, wenn Kontext und Antwort es
 *  erlauben (kannAendern sperrt auch eine gesperrte Firma). */
export function glossarZustand(laden: { stand?: Pick<GlossarAntwort, 'kannAendern'> | null; fehler?: unknown }, rechte: { schreiben: boolean; sicht: boolean }):
  Extract<VorgabeZustand, 'laedt' | 'fehler' | 'nichtLesbar' | 'ohneRecht' | 'erlaubt'> {
  if (rechte.sicht) return 'nichtLesbar';
  const geladen = ladeZustand(laden);
  if (geladen || !laden.stand) return geladen ?? 'laedt';
  return rechte.schreiben && laden.stand.kannAendern ? 'erlaubt' : 'ohneRecht';
}

/** Lesen und Ändern der Vorgaben im Firmenkontext (BAU-FIRMA.md 3). */
export const VORGABEN_RECHTE = { lesen: 'organization.read', schreiben: 'organization.write' } as const;

/** Antwort von GET und PUT /organizations/:id/research-defaults. Ohne gespeicherte Zeile: erlaubt, revision 0. websucheSchaltbar
 *  ist erbeten (BAU-FIRMA.md 6 Punkt 4); fehlt das Feld, gilt der Schalter. */
export interface RechercheVorgabe { webSearchAllowed: boolean; revision: number; websucheSchaltbar?: boolean }

/** Versionskonflikt beim Speichern (409, StaleConflict bietet „Neu laden“). */
export const RECHERCHE_VERALTET = 'STALE_RESEARCH_DEFAULTS';
/** Antwort des Servers in einer Sicht, wenn der Weg dort nicht lesbar ist. */
export const SICHT_NICHT_LESBAR = 'OPERATOR_VIEW_SCOPE';

type Fehlerantwort = { response?: { data?: { code?: unknown; error?: unknown } } };
/** Der Code einer Fehlerantwort des Servers, sonst null. */
export function fehlerCode(fehler: unknown): string | null {
  const code = (fehler as Fehlerantwort)?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/** Der Satz unter einer Zeile, deren Speichern scheiterte: der Text des Servers, sonst der feste Satz. */
export function speicherFehlerSatz(fehler: unknown): string {
  const text = (fehler as Fehlerantwort)?.response?.data?.error;
  return typeof text === 'string' && text.trim() ? text : FIRMEN_VORGABEN_TEXTE.speicherFehler;
}

/** Die Rechte im Firmenkontext (GET /context?ebene=firma). In einer Sicht (Sperrgrund im Kontext oder laufende Sicht im Profil)
 *  wird nie geschrieben, auch wenn der Kontext das Recht nennt; der Server sperrt dort ohnehin. */
export function vorgabenRechte(kontext: { permissions?: string[]; readOnlyReason?: string | null } | null | undefined, sichtAktiv: boolean) {
  const sicht = sichtAktiv || !!kontext?.readOnlyReason;
  return {
    lesen: !!kontext?.permissions?.includes(VORGABEN_RECHTE.lesen),
    schreiben: !sicht && !!kontext?.permissions?.includes(VORGABEN_RECHTE.schreiben),
    sicht,
  };
}

/** Was die Zeile einer Vorgabe zeigt: laedt; fehler (Laden gescheitert, mit „Erneut versuchen“); nichtLesbar (Sicht, der Server
 *  antwortet 403 OPERATOR_VIEW_SCOPE); nichtSchaltbar (Recherche-Art ohne Websuche, Satz statt Schalter); sicht (Wert lesend,
 *  Schalter gesperrt mit NUR_LESEND_GRUND); ohneRecht (Schalter gesperrt mit Satz); erlaubt (Schalter wirkt sofort). */
export type VorgabeZustand = 'laedt' | 'fehler' | 'nichtLesbar' | 'nichtSchaltbar' | 'sicht' | 'ohneRecht' | 'erlaubt';

/** Laden einer Vorgabe: laedt, fehler, nichtLesbar (403 OPERATOR_VIEW_SCOPE) oder null, sobald der Stand da ist. */
export function ladeZustand(laden: { stand?: unknown; fehler?: unknown }): Extract<VorgabeZustand, 'laedt' | 'fehler' | 'nichtLesbar'> | null {
  if (laden.fehler !== undefined && laden.fehler !== null) return fehlerCode(laden.fehler) === SICHT_NICHT_LESBAR ? 'nichtLesbar' : 'fehler';
  return laden.stand ? null : 'laedt';
}

/** Ein geladener Schalter: in einer Sicht lesend, ohne Schreibrecht gesperrt mit Satz, sonst schaltbar. */
const schalterZustand = (rechte: { schreiben: boolean; sicht: boolean }): Extract<VorgabeZustand, 'sicht' | 'ohneRecht' | 'erlaubt'> =>
  (rechte.sicht ? 'sicht' : rechte.schreiben ? 'erlaubt' : 'ohneRecht');

/** Zustand des Schalters „Websuche bei Recherchen erlauben“ (BAU-FIRMA.md 1.6). */
export function rechercheZustand(laden: { stand?: Pick<RechercheVorgabe, 'websucheSchaltbar'> | null; fehler?: unknown },
  rechte: { schreiben: boolean; sicht: boolean }): VorgabeZustand {
  const geladen = ladeZustand(laden);
  if (geladen || !laden.stand) return geladen ?? 'laedt';
  if (laden.stand.websucheSchaltbar === false) return 'nichtSchaltbar';
  return schalterZustand(rechte);
}

/** Die Gruppen der Seite „Benachrichtigungen“ in der Reihenfolge des Servers, nur mit wählbaren Ereignissen. „Sicherheit“
 *  (Pflicht, steht als Satz über den Gruppen) und „Für den Betrieb“ sind nie eine Zeile; der Server lässt sie schon weg. */
export function ereignisGruppen<Z extends Pick<FirmenstandardZeile, 'gruppe' | 'gruppenTitel'>>(ereignisse: Z[]): { gruppe: Gruppe; titel: string; zeilen: Z[] }[] {
  const gruppen: { gruppe: Gruppe; titel: string; zeilen: Z[] }[] = [];
  for (const zeile of ereignisse.filter(z => z.gruppe !== 'sicherheit' && z.gruppe !== 'betrieb')) {
    const da = gruppen.find(g => g.gruppe === zeile.gruppe);
    if (da) da.zeilen.push(zeile);
    else gruppen.push({ gruppe: zeile.gruppe, titel: zeile.gruppenTitel, zeilen: [zeile] });
  }
  return gruppen;
}

/** Was eine Zeile des Firmenstandards zeigt (BAU-FIRMA.md 1.7): Stand des Schalters (wirksam), Zustand nach den Rechten, darunter
 *  die Vorgabe der Plattform, „Vorgabe übernehmen“ nur bei eigenem Stand der Firma (firmenstandard nicht null) und die
 *  Mitzählung nur, wenn die Antwort sie liefert und jemand selbst gewählt hat. */
export function benachrichtigungsZeile(zeile: Pick<FirmenstandardZeile, 'plattform' | 'firmenstandard' | 'wirksam'> & { mitEigenerWahl?: number },
  rechte: { schreiben: boolean; sicht: boolean }, produkt: string) {
  const anzahl = zeile.mitEigenerWahl ?? 0;
  return {
    an: zeile.wirksam,
    zustand: schalterZustand(rechte),
    vorgabe: BENACHRICHTIGUNGS_VORGABEN_TEXTE.vorgabe(produkt, zeile.plattform),
    uebernehmen: zeile.firmenstandard !== null,
    selbstGewaehlt: anzahl > 0 ? BENACHRICHTIGUNGS_VORGABEN_TEXTE.selbstGewaehlt(anzahl) : null,
  };
}
